import { useCallback, useEffect, useRef, useState } from 'react';
import { pixelHeightToGridUnits } from '../gridMetrics';

const GRID_H_EPSILON = 0.15;
const RESIZE_DEBOUNCE_MS = 60;
const MEASURE_BUFFER_PX = 4;

function measurePlaceOrderContentPx(contentEl) {
  if (!contentEl) return 0;

  const mainCardbody = contentEl.parentElement;
  const mainCard = contentEl.closest('.mainCard.BuySellTab');
  if (!mainCardbody || !mainCard) return contentEl.scrollHeight;

  const bodyStyle = window.getComputedStyle(mainCardbody);
  const cardStyle = window.getComputedStyle(mainCard);
  const paddingY = parseFloat(bodyStyle.paddingTop) + parseFloat(bodyStyle.paddingBottom);
  const borderY = parseFloat(cardStyle.borderTopWidth) + parseFloat(cardStyle.borderBottomWidth);

  return contentEl.scrollHeight + paddingY + borderY + MEASURE_BUFFER_PX;
}

/**
 * Measures Place Order content and returns grid `h` for BuySellTab.
 */
export function useBuySellTabAutoHeight(contentEl, { enabled }) {
  const [gridH, setGridH] = useState(null);
  const lastHRef = useRef(null);
  const rafRef = useRef(null);
  const timeoutRef = useRef(null);

  const measureAndUpdate = useCallback(() => {
    if (!contentEl || !enabled) return;

    const measuredPx = measurePlaceOrderContentPx(contentEl);
    if (!measuredPx) return;

    const nextH = pixelHeightToGridUnits(measuredPx);
    const prevH = lastHRef.current;
    if (prevH !== null && Math.abs(nextH - prevH) < GRID_H_EPSILON) return;

    lastHRef.current = nextH;
    setGridH(nextH);
  }, [contentEl, enabled]);

  const scheduleMeasure = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(measureAndUpdate);
    }, RESIZE_DEBOUNCE_MS);
  }, [measureAndUpdate]);

  useEffect(() => {
    if (!enabled || !contentEl) {
      setGridH(null);
      lastHRef.current = null;
      return undefined;
    }

    scheduleMeasure();

    const resizeObserver = new ResizeObserver(() => scheduleMeasure());
    resizeObserver.observe(contentEl);

    const mutationObserver = new MutationObserver(() => scheduleMeasure());
    mutationObserver.observe(contentEl, {
      childList: true,
      subtree: true,
      attributes: true,
      characterData: true,
    });

    window.addEventListener('resize', scheduleMeasure);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      window.removeEventListener('resize', scheduleMeasure);
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [contentEl, enabled, scheduleMeasure]);

  return gridH;
}
